import {BehaviorSubject, Subject, combineLatest, interval, takeWhile, defaultIfEmpty, of} from 'rxjs';
import {switchMap, skipUntil, map, startWith, debounceTime, filter, shareReplay} from 'rxjs/operators';
import {mc} from '@/core';
import {envUtil} from '@/helper';
import {bonusApi} from '@/api/bonus';
import {fullImageUrlFun} from '@/helper/url';
import {activityApi} from '@/api/activity';
import {browser$} from '@/streams/browser';
import {userInfo$} from '@/streams/user';
import {isLogin$, userStatus$} from '@/streams/auth';
import {is_JP_KR$, isKolWhiteListChannel$} from '@/modules/account/overview/stream-kyc';
import {defaultBonusDetail, welcomeBonusDetail$} from '@/modules/register/streams';
import {KOL_REFERRAL_ACTIVITY_STAGE, USER_KOL_REFERRAL_ACTIVITY_STATUS, GetTaskList, TASK_TYPE, TASK_ID_GATHER} from './const';

export const query$ = new Subject();
export const bonusConfig$ = new BehaviorSubject({expiredDays: null, awardText: null});
export const countDownTimeSet$ = new BehaviorSubject(null);

query$
  .pipe(
    skipUntil(browser$),
    switchMap(() => loadBonusConfig())
  )
  .subscribe((x) => bonusConfig$.next(x));

async function loadBonusConfig() {
  const {data, error} = await bonusApi.getWelcomeConfig();
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {expiredDays: null, awardText: null};
}

export const queryTask$ = new Subject();
export const taskList$ = new BehaviorSubject({taskList: null});

queryTask$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadTaskList(query))
  )
  .subscribe((x) => taskList$.next(x));

async function loadTaskList(query) {
  const {data, error} = await bonusApi.getTaskList(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {taskList: null};
}

export const kolReferralActivityQuery$ = new Subject();

export const kolReferralCode$ = new BehaviorSubject('');

export const kolReferralActivityId$ = new BehaviorSubject('');

export const kolReferralActivityCountDown$ = new BehaviorSubject(0);

export const bonusCurrency$ = new BehaviorSubject('');

export const userBonus$ = new BehaviorSubject(0);

export const isUserBonusClaimed$ = new BehaviorSubject(false);

export const userDeposit$ = new BehaviorSubject(0);

export const userTradeVolume$ = new BehaviorSubject(0);

export const kolActivityCurrentStage$ = new BehaviorSubject(0);

export const bonusStages$ = new BehaviorSubject([]);

export const userBonusStageIndex$ = combineLatest([bonusStages$, userBonus$]).pipe(
  debounceTime(100),
  map(([stages, userBonus]) => {
    for (let i = 0; i < stages.length; i++) {
      if (userBonus < stages[i].bonus) {
        return i;
      }
    }
    return stages.length - 1;
  })
);

export const kolInfo$ = new BehaviorSubject({
  avatar: '',
  name: '',
});

export const displayWelcomeRewardsTab$ = combineLatest([taskList$, userInfo$, userStatus$, kolReferralActivityId$]).pipe(
  map(([{taskList}, userInfo, userStatus, kolReferralActivityId]) => {
    // 参加了kol 充值交易活动
    if (kolReferralActivityId) {
      return true;
    }
    if (userStatus === 'Logout') {
      return true;
    }
    // 创建账户30天内
    if (userInfo && userInfo.createTime) {
      const createTime = userInfo.createTime;
      const day = 1000 * 60 * 60 * 24;
      const cha = Date.now() - (createTime + day * 30);
      if (cha < 0) {
        return true;
      }
    }
    // 有已完成的任务
    return taskList && taskList.find(({status}) => status === 'SUCCESS');
  })
);

export const kolWelcomeRewards$ = bonusStages$.pipe(
  map((list) => {
    const val = list.reduce((max, {bonus}) => {
      return Math.max(max, bonus);
    }, 0);
    return val;
  }, startWith(0))
);

kolReferralActivityQuery$
  .pipe(
    skipUntil(browser$),
    skipUntil(isLogin$.pipe(filter(Boolean))),
    switchMap((query) => combineLatest([activityApi.getUserKolExclusiveActivityInfo(), userInfo$])),
    shareReplay(1)
  )
  .subscribe(([{data}, userInfo]) => {
    if (!data) {
      return;
    }
    const activityInfo = data.extInfo.activityInfo;
    const startTime = userInfo.createTime;
    kolReferralActivityCountDown$.next(0);
    bonusCurrency$.next(activityInfo.currency);
    kolReferralActivityId$.next(data.activityId);
    kolInfo$.next({
      avatar: fullImageUrlFun()('7ec0d398bea20b43.png'),
      name: activityInfo.extInfo.nickName,
    });
    const bonusStages = activityInfo.extInfo.ladderRewardsDTOS.map(({bonusRewards, netDepositAmount, tradeAmount}) => {
      return {
        bonus: bonusRewards,
        trade: tradeAmount,
        deposit: netDepositAmount,
      };
    });
    kolReferralCode$.next(activityInfo.bizCode);
    bonusStages$.next(bonusStages);
    userBonus$.next(data.rewardAmount);
    userDeposit$.next(data.extInfo.netDepositAmount);
    userTradeVolume$.next(data.extInfo.tradeAmount);
    const isUserBonusClaimed = [USER_KOL_REFERRAL_ACTIVITY_STATUS.CLAIMED, USER_KOL_REFERRAL_ACTIVITY_STATUS.AT_RISK].includes(data.status);
    isUserBonusClaimed$.next(isUserBonusClaimed);
    if (activityInfo.status === 200) {
      const now = Date.now();
      // 入金+交易期
      const missionEndTime = new Date(startTime + days(activityInfo.extInfo.depositDays)).getTime();
      // 风险控制期
      const riskMonitorEndTime = new Date(startTime + days(activityInfo.extInfo.rewardDays)).getTime();
      // 奖励领取期
      const claimEndTime = new Date(startTime + days(activityInfo.extInfo.rewardOverDays)).getTime();
      // Ends
      const endTime = activityInfo.endTime;
      if (now > endTime || data.status === USER_KOL_REFERRAL_ACTIVITY_STATUS.END) {
        kolActivityCurrentStage$.next(KOL_REFERRAL_ACTIVITY_STAGE.END);
        return;
      }
      if (now < missionEndTime) {
        kolReferralActivityCountDown$.next(Math.min(missionEndTime, endTime));
        kolActivityCurrentStage$.next(KOL_REFERRAL_ACTIVITY_STAGE.DEPOSIT_TRADE);
        return;
      }
      if (now < riskMonitorEndTime) {
        kolReferralActivityCountDown$.next(Math.min(riskMonitorEndTime, endTime));
        kolActivityCurrentStage$.next(KOL_REFERRAL_ACTIVITY_STAGE.RISK_MONITORING);
        return;
      }
      if (now < claimEndTime) {
        if (!isUserBonusClaimed) {
          kolReferralActivityCountDown$.next(Math.min(claimEndTime, endTime));
        }
        kolActivityCurrentStage$.next(KOL_REFERRAL_ACTIVITY_STAGE.BONUS_CLAIM);
        return;
      }
      if (now > claimEndTime) {
        kolActivityCurrentStage$.next(KOL_REFERRAL_ACTIVITY_STAGE.BONUS_CLAIM_EXPIRED);
      }
    }
  });

function days(count) {
  return count * 24 * 60 * 60 * 1000;
}

function formatRemainingTime(milliseconds) {
  // 计算天数
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  // 计算剩余的毫秒数，去掉天数后的毫秒数
  const remainingAfterDays = milliseconds % (1000 * 60 * 60 * 24);
  // 计算小时数
  const hours = Math.floor(remainingAfterDays / (1000 * 60 * 60));
  // 计算剩余的毫秒数，去掉小时后的毫秒数
  const remainingAfterHours = remainingAfterDays % (1000 * 60 * 60);
  // 计算分钟数
  const minutes = Math.floor(remainingAfterHours / (1000 * 60));
  // 计算剩余的毫秒数，去掉分钟后的毫秒数
  const remainingAfterMinutes = remainingAfterHours % (1000 * 60);
  // 计算秒数
  const seconds = Math.floor(remainingAfterMinutes / 1000);

  // 格式化输出，确保每个部分都是两位数
  return {
    d: days,
    h: hours,
    m: minutes,
    s: seconds,
  };
}

/**
 * 获取 welcome bonus 有效期限
 * expiredDays: 有效期，单位 D
 * */
export async function getWelcomeConfig() {
  const {data} = await bonusApi.getWelcomeConfig();
  if (data) {
    return data;
  }
  return {expiredDays: null, awardText: null};
}

// 计算离失效的剩余时间
const getRemainingTime = (createTime = 0, expiredDays = 30) => {
  const now = Date.now();
  const day = 1000 * 60 * 60 * 24;
  if (createTime === 0) {
    return 0;
  }
  return createTime + Number(expiredDays) * day - now;
};

/**
 * 倒计时，是否过了有效期
 * countDownTimeSet$: 自定义倒计时时间，案例场景：未登录时按30天倒计时
 * */
export const expiredRemainingTimestamp$ = combineLatest([userInfo$, welcomeBonusDetail$, countDownTimeSet$]).pipe(
  switchMap(([userInfo, welcomeBonusDetail, countDownTimeSet]) => {
    const createTime = countDownTimeSet && countDownTimeSet > 0 ? countDownTimeSet : userInfo.createTime || 0;
    const expiredDays = Number(welcomeBonusDetail.showDays || 30);
    if (envUtil.isServer) {
      return of(getRemainingTime(createTime, expiredDays));
    } else {
      return interval(1e3).pipe(
        map(() => getRemainingTime(createTime, expiredDays)),
        startWith(getRemainingTime(createTime, expiredDays))
      );
    }
  }),
  takeWhile((leftTime) => {
    return Number(leftTime) >= 0;
  }),
  // 提示过期， 也为了不中断其他流的执行
  defaultIfEmpty(null),
  shareReplay(1)
);

// 格式化倒计时：dd, hh, mm, ss
export const expiredRemainingFormatter$ = expiredRemainingTimestamp$.pipe(
  filter(Boolean),
  map((leftTime) => formatRemainingTime(leftTime)),
  startWith({d: 0, h: 0, m: 0, s: 0}),
  shareReplay(1)
);

// 是否是新用户
export const isNewWelcomeUser$ = combineLatest([userInfo$, welcomeBonusDetail$]).pipe(
  filter(([userInfo, welcomeBonusDetail]) => !!userInfo.createTime && !!welcomeBonusDetail.entryTime),
  map(([userInfo, welcomeBonusDetail]) => userInfo.createTime > Number(welcomeBonusDetail.entryTime)),
  shareReplay(1)
);

// 已过期: 非新用户并且不在效期内
export const isExpired$ = expiredRemainingTimestamp$.pipe(map((leftTime) => !(leftTime && Number((leftTime / 1e3).toFixed(0)) > 0)));

const taskListSort = (list, order) => {
  const newList = list.filter((item) => order.includes(item.taskId));
  newList.sort((a, b) => {
    const indexA = order.indexOf(a.taskId);
    const indexB = order.indexOf(b.taskId);

    // 比较索引并返回排序结果
    if (indexA < indexB) return -1;
    if (indexA > indexB) return 1;
    return 0;
  });
  return newList;
};

// 任务列表
export const rewardsTaskListNew$ = combineLatest([isLogin$, is_JP_KR$, isKolWhiteListChannel$, kolInfo$, taskList$, isExpired$]).pipe(
  // filter(([, , taskListStatus]) => !!taskListStatus?.taskList?.length),
  map(([isLogin, is_JP_KR, isKolWhiteListChannel, kolInfo, taskListStatus, isExpired]) => {
    const kycStatus = (taskListStatus?.taskList || []).find((o) => o.taskId === TASK_ID_GATHER.KYC)?.status;
    const isKycInit = !kycStatus || kycStatus === 'INIT';
    const list = GetTaskList(is_JP_KR, isKolWhiteListChannel, isLogin, isKycInit);
    const taskStatesList = taskListSort(
      taskListStatus?.taskList || [],
      list.map((item) => item.taskId)
    );
    // kol信息存在时，前置插入一个任务
    if (kolInfo?.name) {
      list.push({
        type: TASK_TYPE.KOL_REFERRAL_ACTIVITY,
        id: Date.now(),
        stepStatus: 'NOT_STARTED',
      });
    }
    const target = [...taskStatesList].reverse().find((x) => ['SUCCESS', 'AWARDS_SUCCESS'].includes(x.status));
    const targetIdx = target ? list.findIndex((x) => x.taskId === target.taskId) : -1;
    if (targetIdx < 0) {
      list[0].stepStatus = 'ACTIVE';
    }
    if (targetIdx === list.length - 2) {
      list[list.length - 1].stepStatus = 'ACTIVE';
    }
    return list.map((o, idx) => {
      let item = taskStatesList.find((i) => i.taskId === o.id);
      if (item) {
        const status = isExpired && (item.status === 'INIT' || item.status === 'SUCCESS') ? 'EXPIRE' : item.status;
        const stepStatus = (targetIdx >= 0 && targetIdx >= idx && 'FINISH') || (targetIdx >= 0 && targetIdx === idx - 1 && 'ACTIVE') || o.stepStatus;
        return {...o, ...item, status, stepStatus};
      }
      return {...o, stepStatus: isLogin ? o.stepStatus : 'NOT_LOGIN'};
    });
  }),
  startWith(GetTaskList())
);

// 奖励Amount，根据是否过效期 / 是否新老用户，决定是否取新值/旧值; 未登录时取新值；
export const welcomeBonusAmount$ = combineLatest([isLogin$, isNewWelcomeUser$, isExpired$, welcomeBonusDetail$]).pipe(
  map(([isLogin, isNewUser, isExpired, welcomeBonusDetail]) => {
    return (isExpired || !isNewUser) && isLogin ? welcomeBonusDetail.oldBonusAmount : welcomeBonusDetail.newBonusAmount || defaultBonusDetail.newBonusAmount;
  }),
  startWith(defaultBonusDetail.newBonusAmount),
  shareReplay(1)
);
